import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  Box,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  CardContent,
  ImageList,
  ImageListItem,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function ImageGenerator() {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userImages, setUserImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState({ open: false, message: '', severity: 'info' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, imageId: null });
  const [usageLimit, setUsageLimit] = useState(null);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const navigate = useNavigate();

  // Fetch user's images when component mounts
  useEffect(() => {
    fetchUserImages();
  }, []);

  // Also fetch after new image is generated
  useEffect(() => {
    if (generatedImage) {
      fetchUserImages();
    }
  }, [generatedImage]);

  // Add useEffect to check usage limits when component mounts
  useEffect(() => {
    checkUsageLimit();
  }, []);

  const fetchUserImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/user-images`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Transform the URLs to include the full path
      const imagesWithFullUrls = response.data.map(image => ({
        ...image,
        url: `${API_URL.replace('/api', '')}${image.url}`
      }));
      setUserImages(imagesWithFullUrls);
    } catch (error) {
      console.error('Error fetching user images::', error);
    }
  };

  const checkUsageLimit = async () => {
    try {
      const response = await axios.get(`${API_URL}/generate/usage`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsageLimit(response.data);
      setHasReachedLimit(!response.data.canGenerate);
    } catch (err) {
      console.error('Failed to check usage limit:', err);
    }
  };

  const handleGenerateImage = async () => {
    if (hasReachedLimit) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };
      
      const response = await axios.post(
        `${API_URL}/generate-image`, 
        {
          prompt: prompt,
          negative_prompt: "Unrealistic skin, excessive smoothness, low-resolution details, anime-like features, plastic-looking face, asymmetrical features, over-sharpened, strange anatomy, poorly detailed hands, deformed eyes, low-quality textures, unnatural lighting.",
          num_steps: 50,
          guidance_scale: 7
        },
        config
      );
      
      const taskId = response.data.task_id;

      while (true) {
        const pollResponse = await axios.get(
          `${API_URL}/task/${taskId}?prompt=${encodeURIComponent(prompt)}`,
          config
        );
        
        const result = pollResponse.data;
        
        if (result.status === 'completed') {
          setGeneratedImage(`${API_URL.replace('/api', '')}${result.imageUrl}`);
          // Update usage limits after successful generation
          await checkUsageLimit();
          // Refresh profile page stats
          window.dispatchEvent(new Event('imageGenerated'));
          break;
        }
        
        if (result.status === 'failed') {
          throw new Error('Image generation failed');
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please sign in again to continue.');
      } else if (err.response?.status === 429) {
        // Rate limit exceeded
        setHasReachedLimit(true);
        setError(err.response.data.details.message);
      } else {
        setError('Failed to generate image. Please try again.');
      }
      console.error('Error generating image:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const downloadImage = async (imageUrl, prompt) => {
    try {
      setDownloadStatus({ 
        open: true, 
        message: 'Starting download...', 
        severity: 'info' 
      });

      // Extract just the path portion of the URL and remove duplicate /api if present
      const urlPath = imageUrl
        .replace(API_URL.replace('/api', ''), '')
        .replace('/api', '');
      
      // Fetch the image as a blob
      const response = await fetch(`${API_URL}${urlPath}`);
      const blob = await response.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary link element
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}.png`;
      link.download = filename;
      
      // Append to document, click, and cleanup
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      setDownloadStatus({ 
        open: true, 
        message: `Image "${filename}" is being downloaded to your computer`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error downloading image:', error);
      setDownloadStatus({ 
        open: true, 
        message: 'Failed to download image', 
        severity: 'error' 
      });
    }
  };

  const handleDownload = (imageUrl, prompt) => {
    downloadImage(imageUrl, prompt);
  };

  const handleCloseSnackbar = () => {
    setDownloadStatus({ ...downloadStatus, open: false });
  };

  const handleDeleteClick = (imageId, e) => {
    e.stopPropagation(); // Prevent modal from opening
    setDeleteConfirm({ open: true, imageId });
  };

  const handleDeleteConfirm = async () => {
    try {
      setDownloadStatus({ 
        open: true, 
        message: 'Deleting image...', 
        severity: 'info' 
      });

      await axios.delete(`${API_URL}/images/${deleteConfirm.imageId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Remove image from state
      setUserImages(userImages.filter(img => img._id !== deleteConfirm.imageId));
      
      // Close modal if the deleted image was being previewed
      if (selectedImage?._id === deleteConfirm.imageId) {
        setSelectedImage(null);
      }

      setDownloadStatus({ 
        open: true, 
        message: 'Image deleted successfully', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      setDownloadStatus({ 
        open: true, 
        message: 'Failed to delete image', 
        severity: 'error' 
      });
    } finally {
      setDeleteConfirm({ open: false, imageId: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, imageId: null });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 6,
          backgroundColor: '#0a0b0e',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          align="center"
          sx={{ 
            fontWeight: 600,
            color: '#00ffea',
            mb: 3
          }}
        >
          Image Generator
        </Typography>
        
        <Typography 
          variant="h5" 
          align="center"
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 6
          }}
        >
          Enter a description of the image you want to generate
        </Typography>

        {hasReachedLimit && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/pricing')}
              >
                Upgrade Plan
              </Button>
            }
          >
            You've reached your monthly image generation limit.
            {usageLimit && ` (${usageLimit.used} of ${usageLimit.limit} images used)`}
            <br />
            Upgrade your plan to generate more images.
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Image Description"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={hasReachedLimit}
          sx={{ 
            mb: 6,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#141519',
              borderRadius: 4,
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)'
              }
            },
            '& .MuiOutlinedInput-input': {
              color: 'white',
              fontSize: '1.1rem'
            }
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleGenerateImage}
            disabled={!prompt || loading || hasReachedLimit}
            sx={{ 
              minWidth: 200,
              height: 48,
              borderRadius: 50,
              background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
              color: '#000',
              fontSize: '1.1rem',
              fontWeight: 500,
              textTransform: 'none',
              '& .MuiSvgIcon-root': {
                color: '#000'
              },
              '&:hover': {
                background: 'linear-gradient(90deg, #00b8ff 0%, #00ffea 100%)',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#000' }} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#000' }}>
                <ImageIcon sx={{ mr: 1, color: '#000' }} /> 
                <span style={{ color: '#000' }}>Generate Image</span>
              </Box>
            )}
          </Button>
        </Box>

        {error && (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Typography color="error">
              {error}
            </Typography>
          </Box>
        )}

        {generatedImage && (
          <Card 
            elevation={2}
            sx={{ 
              mt: 4,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <CardMedia
              component="img"
              image={generatedImage}
              alt="Generated artwork"
              sx={{ 
                width: '100%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'contain'
              }}
            />
            <CardActions sx={{ justifyContent: 'center', p: 2 }}>
              <Button 
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => downloadImage(generatedImage.replace('http://localhost:6001', ''), prompt)}
                sx={{ 
                  borderRadius: 2,
                  px: 3
                }}
              >
                Download Image
              </Button>
            </CardActions>
          </Card>
        )}

        {/* Recent Generations Section */}
        {userImages.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 4,
                color: '#fff',
                fontWeight: 600
              }}
            >
              Your Recent Generations
            </Typography>
            <Grid container spacing={3}>
              {userImages.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={image._id}>
                  <Paper
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& .image-overlay': {
                          opacity: 1
                        }
                      }
                    }}
                    onClick={() => handleImageClick(image)}
                  >
                    <Box
                      component="img"
                      src={image.url}
                      alt={image.prompt}
                      sx={{
                        width: '100%',
                        height: 240,
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    <Box
                      className="image-overlay"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        p: 2,
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      }}
                    >
                      <Typography variant="body2" sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {image.prompt}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ZoomOutMapIcon sx={{ color: '#00ffea', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: '#00ffea' }}>
                            Click to view full size
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(image._id, e)}
                          sx={{ 
                            color: '#ff4444',
                            '&:hover': {
                              bgcolor: 'rgba(255, 68, 68, 0.1)'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Image Preview Modal */}
        <Dialog
          open={!!selectedImage}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#0a0b0e',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedImage && (
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={selectedImage.url}
                  alt={selectedImage.prompt}
                  onClick={handleCloseModal}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pointerEvents: 'auto'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'white',
                      flexGrow: 1,
                      mr: 2
                    }}
                  >
                    {selectedImage.prompt}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(selectedImage._id, e);
                      }}
                      sx={{
                        bgcolor: '#ff4444',
                        '&:hover': {
                          bgcolor: '#ff6666'
                        }
                      }}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(selectedImage.url, selectedImage.prompt);
                      }}
                      sx={{
                        bgcolor: '#00ffea',
                        color: '#000',
                        '&:hover': {
                          bgcolor: '#00b8ff'
                        }
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={downloadStatus.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={downloadStatus.severity}
            sx={{ 
              width: '100%',
              bgcolor: downloadStatus.severity === 'success' ? 'primary.main' : undefined,
              color: downloadStatus.severity === 'success' ? 'black' : undefined
            }}
          >
            {downloadStatus.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={deleteConfirm.open}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: {
              bgcolor: '#1a1b1e',
              color: 'white',
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ color: '#ff4444' }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleDeleteCancel}
              sx={{ 
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                bgcolor: '#ff4444',
                color: 'white',
                '&:hover': {
                  bgcolor: '#ff6666'
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default ImageGenerator; 